/**
 * Máquina de estados de reserva + pago (lógica pura, sin dependencias de
 * infraestructura → consumible por functions, panel y widget, y testeable
 * de forma aislada). Ver docs/architecture/MAQUINA-DE-ESTADOS.md.
 */
import type { ReservationStatus, PaymentStatus } from './types'

/* ============================================================
 * Transiciones de reserva
 * ============================================================ */
const RESERVATION_TRANSITIONS: Record<ReservationStatus, ReservationStatus[]> = {
  buscando: ['opciones_mostradas', 'error_proveedor'],
  opciones_mostradas: ['seleccion', 'buscando', 'error_proveedor'],
  // Vaciar el carrito devuelve al cliente a mirar opciones. Antes no existía
  // ese camino porque una reserva era una sola tarifa y elegir era definitivo.
  seleccion: ['tarifa_revalidada', 'requote_requerido', 'error_proveedor', 'opciones_mostradas'],
  tarifa_revalidada: ['datos_pasajeros', 'requote_requerido', 'error_proveedor'],
  datos_pasajeros: ['lista_para_reservar', 'requote_requerido', 'error_proveedor'],
  lista_para_reservar: ['reservando', 'requote_requerido', 'error_proveedor'],
  reservando: ['confirmada', 'requote_requerido', 'error_proveedor'],
  confirmada: ['cancelada'],
  // Una tarifa expirada vuelve a revalidarse antes de continuar.
  requote_requerido: ['tarifa_revalidada', 'error_proveedor'],
  // El error del proveedor deja el hilo listo para retomar (o que lo tome un humano).
  error_proveedor: ['seleccion', 'buscando', 'lista_para_reservar'],
  cancelada: [],
}

/** Estados terminales de la reserva (no admiten más transiciones). */
export function isTerminalReservation(status: ReservationStatus): boolean {
  return RESERVATION_TRANSITIONS[status].length === 0
}

export function canTransitionReservation(
  from: ReservationStatus,
  to: ReservationStatus,
): boolean {
  return RESERVATION_TRANSITIONS[from]?.includes(to) ?? false
}

/**
 * Desde dónde empieza un reintento.
 *
 * Un intento de compra que murió a media transacción —el cobro falló, se cayó
 * la red— deja la reserva en un estado intermedio, y desde ahí la máquina no
 * deja volver a revalidar tarifas. El efecto era peor que el error original:
 * **la venta quedaba muerta** y el cliente no podía reintentar ni cambiando de
 * tarjeta.
 *
 * Reintentar es legítimo: el cliente le da otra vez a pagar. Así que los
 * estados que deja atrás el flujo de compra rebobinan a `seleccion` y el
 * proceso corre completo de nuevo. Eso además es lo correcto: entre un intento
 * y otro los precios pudieron moverse, y revalidar es justo lo que hay que
 * hacer.
 *
 * Los demás estados se devuelven tal cual: `confirmada` no se reintenta,
 * `error_proveedor` tiene su propio camino de vuelta.
 */
const INTERMEDIOS_DE_COMPRA: ReservationStatus[] = [
  'tarifa_revalidada', 'datos_pasajeros', 'lista_para_reservar',
]

export function desdeDondeSeReintenta(status: ReservationStatus): ReservationStatus {
  return INTERMEDIOS_DE_COMPRA.includes(status) ? 'seleccion' : status
}

/** Lanza si la transición no está permitida (guarda para uso server-side). */
export function assertTransitionReservation(
  from: ReservationStatus,
  to: ReservationStatus,
): void {
  if (!canTransitionReservation(from, to)) {
    throw new Error(`Transición de reserva inválida: ${from} → ${to}`)
  }
}

/* ============================================================
 * Transiciones de pago
 * ============================================================ */
const PAYMENT_TRANSITIONS: Record<PaymentStatus, PaymentStatus[]> = {
  /* Riel tarjeta, dos caminos.
   *
   * El bueno: `autorizado` (dinero retenido) → `capturado` al confirmar, o
   * `anulado` si algo falla. El cliente nunca ve un cargo que haya que
   * devolverle.
   *
   * El otro: la tarjeta no admite retener —débito, sobre todo— y se cobra de
   * una. Ahí `pago_requerido` va directo a `capturado`, y deshacer ya no es
   * soltar una retención sino devolver dinero: `capturado → reembolsado`.
   * Ver ADR-0002 y el paso 5a de reservationBook. */
  pago_requerido: ['autorizado', 'capturado', 'rechazado', 'pago_offline_pendiente'],
  //
  // `cobro_fallido` es el caso feo: la reserva quedó en el proveedor pero la
  // captura no entró. No es terminal a propósito — la autorización sigue viva
  // un rato, así que un humano puede reintentar el cobro (→ capturado) o
  // liberar el dinero y cancelar la reserva (→ anulado). Ver docs/COMO-SE-COBRA.md.
  autorizado: ['capturado', 'anulado', 'cobro_fallido'],
  cobro_fallido: ['capturado', 'anulado'],
  // Un cobro se devuelve: por cancelación del cliente, o porque el viaje no se
  // pudo confirmar y el dinero ya estaba cobrado.
  capturado: ['reembolsado'],
  reembolsado: [],
  anulado: [],
  rechazado: [],
  // Riel offline (reservar → conciliar 24h → pagado / expirado)
  pago_offline_pendiente: ['pagado_offline', 'expirado'],
  pagado_offline: [],
  expirado: [],
}

export function isTerminalPayment(status: PaymentStatus): boolean {
  return PAYMENT_TRANSITIONS[status].length === 0
}

export function canTransitionPayment(
  from: PaymentStatus,
  to: PaymentStatus,
): boolean {
  return PAYMENT_TRANSITIONS[from]?.includes(to) ?? false
}

export function assertTransitionPayment(
  from: PaymentStatus,
  to: PaymentStatus,
): void {
  if (!canTransitionPayment(from, to)) {
    throw new Error(`Transición de pago inválida: ${from} → ${to}`)
  }
}

/* ============================================================
 * Estado efectivo de cara al cliente (combina reserva × pago)
 * ============================================================ */
export type EffectiveCode =
  | 'pagada_confirmada'
  | 'reservada_esperando_pago'
  | 'pago_retenido'
  | 'no_concretada'
  | 'en_proceso'

export interface EffectiveStatus {
  code: EffectiveCode
  label: string
}

/**
 * Traduce la combinación (estado de reserva, estado de pago) al mensaje que
 * ve el cliente. Ver la tabla de MAQUINA-DE-ESTADOS.md.
 */
export function effectiveStatus(
  reservation: ReservationStatus,
  payment?: PaymentStatus,
): EffectiveStatus {
  if (
    reservation === 'confirmada' &&
    (payment === 'capturado' || payment === 'pagado_offline')
  ) {
    return { code: 'pagada_confirmada', label: 'Reserva pagada y confirmada' }
  }
  if (reservation === 'confirmada' && payment === 'pago_offline_pendiente') {
    return {
      code: 'reservada_esperando_pago',
      label: 'Reservado, esperando tu transferencia (24h)',
    }
  }
  if (reservation === 'lista_para_reservar' && payment === 'autorizado') {
    return { code: 'pago_retenido', label: 'Pago retenido, confirmando reserva…' }
  }
  if (
    reservation === 'cancelada' ||
    payment === 'anulado' ||
    payment === 'expirado' ||
    payment === 'rechazado'
  ) {
    return { code: 'no_concretada', label: 'No se concretó (sin cargo)' }
  }
  return { code: 'en_proceso', label: 'En proceso' }
}
